import type { ReactNode } from 'react';
import { ArrowRight, BarChart3, Box, Link2, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ANALYSIS_ROUTE } from '@/components/analysis/reportAnalysisUi';

function AmazonPlatformMark() {
  return (
    <img
      src="/amazon.svg"
      alt=""
      aria-hidden
      className="h-10 w-10 shrink-0 object-contain"
    />
  );
}

function FlipkartPlatformMark() {
  return (
    <img
      src="/flipkart.svg"
      alt=""
      aria-hidden
      className="h-10 w-10 shrink-0 object-contain"
    />
  );
}

export function PlatformAccountSection({
  platform,
  title,
  description,
  children,
}: {
  platform: 'amazon' | 'flipkart';
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface px-4 py-3 shadow-sm sm:px-5">
      <div className="flex items-center gap-3">
        {platform === 'amazon' ? <AmazonPlatformMark /> : <FlipkartPlatformMark />}
        <div className="min-w-0">
          <h2 className="text-base font-semibold leading-tight text-brand-ink">{title}</h2>
          <p className="mt-0.5 text-body-sm text-foreground-muted">{description}</p>
        </div>
      </div>
      <div className="mt-2">{children}</div>
    </section>
  );
}

export function EmptyAccountState({ title, detail }: { title: string; detail: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl bg-surface-muted px-4 py-3 text-center">
      <span className="mb-1.5 flex h-8 w-8 items-center justify-center rounded-lg border border-border bg-surface text-foreground-subtle shadow-sm">
        <Box className="h-4 w-4" strokeWidth={1.75} aria-hidden />
      </span>
      <p className="text-body-md font-medium text-foreground">{title}</p>
      <p className="mt-0.5 max-w-md text-body-sm text-foreground-muted">{detail}</p>
    </div>
  );
}

export function ConnectedPlatformSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-2 text-headline-md font-semibold text-brand-ink">{title}</h2>
      {children}
    </section>
  );
}

export function ConnectAccountsCta({ hasAccounts }: { hasAccounts: boolean }) {
  return (
    <section className="relative overflow-hidden rounded-2xl border border-brand/15 bg-brand-subtle px-4 py-5 text-center">
      <div
        className="pointer-events-none absolute -left-10 top-1/2 h-28 w-28 -translate-y-1/2 rounded-full bg-white/70"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/50"
        aria-hidden
      />
      <div className="relative mx-auto flex max-w-lg flex-col items-center">
        <span className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl border border-brand/10 bg-surface text-brand shadow-sm">
          <Link2 className="h-4 w-4" aria-hidden />
        </span>
        <h2 className="text-base font-semibold text-brand-ink">
          {hasAccounts ? 'Connect more advertising accounts' : 'Connect your advertising accounts'}
        </h2>
        <p className="mt-1 text-body-sm text-foreground-muted">
          {hasAccounts
            ? 'Connect additional Amazon or Flipkart accounts from the Connections page.'
            : 'Connect Amazon or Flipkart from the Connections page to get started.'}
        </p>
        <Link
          to="/connections"
          className="mt-4 inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-brand-ink px-3.5 text-body-md font-medium text-brand-foreground hover:bg-chrome-border"
        >
          Go to Connections
          <ArrowRight className="h-4 w-4" aria-hidden />
        </Link>
      </div>
    </section>
  );
}

export function AnalysisActionBar() {
  return (
    <section className="flex flex-col gap-3 rounded-2xl border border-border bg-surface px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:px-5">
      <div className="flex min-w-0 items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-subtle text-brand">
          <BarChart3 className="h-5 w-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-base font-semibold leading-tight text-brand-ink">Analysis</p>
          <p className="mt-0.5 text-body-sm text-foreground-muted">
            Get audits for your connected ad accounts and choose the date range.
          </p>
        </div>
      </div>
      <Link
        to={ANALYSIS_ROUTE}
        className="inline-flex h-9 shrink-0 items-center justify-center gap-1.5 self-start rounded-lg bg-brand-ink px-3.5 text-body-md font-medium text-brand-foreground hover:bg-chrome-border sm:self-center"
      >
        <Sparkles className="h-4 w-4" aria-hidden />
        Get analysis
      </Link>
    </section>
  );
}
